/**
 * Backend Historical Reports Page
 * 
 * Updated to use backend API with proper data transformation
 * and error handling following REACT_INTEGRATION.md patterns
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBackendHistoricalReports } from '../hooks/useBackendHistoricalReports';
import { HistoricalReportDetail, DataSource, Folder } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { HistoricalReportsErrorBoundary } from '../components/historical/HistoricalReportsErrorBoundary';
import { HistoricalReportsErrorDisplay } from '../components/historical/HistoricalReportsErrorDisplay';
import { fetchDataAdmissionBootstrap } from '../services/dataAdmissionService';
import { useTranslate } from '../hooks/useTranslate';
import ContextualHelpLink from '../components/training/ContextualHelpLink';
import * as sharedReportService from '../services/sharedReportService';
import type { SaveSharedReportResponse } from '../types/shared';
import { HiShare } from 'react-icons/hi';
import { TbReportAnalytics } from 'react-icons/tb';

// Date formatting utility
function formatDate(iso: string): string {
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

// Format health score for display
function formatHealthScore(score: number | string | null): string {
    if (score === null || score === undefined) return 'N/A';
    let numeric: number | null = null;
    if (typeof score === 'number') numeric = score;
    else if (typeof score === 'string') {
        const trimmed = score.trim().replace(/%$/, '');
        const parsed = parseFloat(trimmed);
        if (!Number.isNaN(parsed)) numeric = parsed;
    }
    if (numeric === null) return 'N/A';
    return `${numeric.toFixed(1)}%`;
}

// Status badge component (white/red/grey theme)
const StatusBadge = ({ status }: { status: string }) => {
    const { t } = useTranslate();
    // Normalize backend status values (analyzed/analyzing/error)
    const normalized = status === 'processed' ? 'analyzed' : status === 'processing' ? 'analyzing' : status;
    const styles: Record<string, string> = {
        analyzed: 'bg-gray-100 text-gray-700 border-gray-300',
        analyzing: 'bg-red-50 text-red-700 border-red-300',
        error: 'bg-red-100 text-red-800 border-red-400',
        default: 'bg-gray-100 text-gray-600 border-gray-300',
    };
    const labels: Record<string, string> = {
        analyzed: t('Analyzed'),
        analyzing: t('Analyzing'),
        error: t('Error'),
    };
    const styleClass = styles[normalized] || styles.default;
    const displayLabel = labels[normalized] || normalized;
    return <span className={`px-2 py-1 rounded-full text-xs font-medium border ${styleClass}`}>{displayLabel}</span>;
};

interface DetailModalProps {
    open: boolean;
    onClose: () => void;
    reportId: string | null;
    loadDetail: (id: string) => Promise<HistoricalReportDetail | null>;
}

const DetailModal = ({ open, onClose, reportId, loadDetail }: DetailModalProps) => {
    const { t } = useTranslate();
    const [detail, setDetail] = useState<HistoricalReportDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        if (open && reportId) {
            setLoading(true);
            setError(null);
            loadDetail(reportId)
                .then(d => {
                    if (active) setDetail(d);
                })
                .catch(e => {
                    if (active) {
                        const message = e?.userMessage || e?.message || t('Failed to load report detail');
                        setError(message);
                    }
                })
                .finally(() => {
                    if (active) setLoading(false);
                });
        } else {
            setDetail(null);
        }
        return () => { active = false; };
    }, [open, reportId, loadDetail]);

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true" aria-labelledby="historical-detail-title">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative bg-gray-900 border border-gray-700 rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
                <header className="px-6 py-4 border-b border-gray-700 flex justify-between items-start">
                    <div>
                        <h2 id="historical-detail-title" className="text-xl font-semibold text-white">
                            {t('Report Detail')} #{detail?.version || reportId}
                        </h2>
                        {detail && (
                            <p className="text-sm text-gray-400 mt-1">
                                {t('Generated:')} {formatDate(detail.generated_at)}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-white transition-colors"
                        aria-label="Close detail modal"
                    >
                        ✕
                    </button>
                </header>

                <div className="p-6 overflow-y-auto space-y-6 text-sm">
                    {loading && (
                        <div className="flex items-center justify-center py-8">
                            <div className="loader-red loader-md"></div>
                            <span className="ml-3 text-gray-400">{t('Loading report details...')}</span>
                        </div>
                    )}

                    {error && (
                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                            <p className="text-red-400">{t('Error')}: {error}</p>
                        </div>
                    )}

                    {detail && !loading && (
                        <>
                            {/* Report Summary */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <Metric
                                    label={t('Health Score')}
                                    value={formatHealthScore(detail.health_score_overall)}
                                />
                                <Metric
                                    label={t('Issues Found')}
                                    value={detail.issue_count || 0}
                                />
                                <Metric
                                    label={t('Status')}
                                    value={<StatusBadge status={detail.status} />}
                                />
                                <Metric
                                    label={t('Version')}
                                    value={detail.version}
                                />
                            </div>

                            {/* Additional Metadata */}
                            <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                                <h3 className="text-sm font-semibold text-white mb-3">{t('Report Metadata')}</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                    <div>
                                        <span className="text-gray-400">{t('Report ID')}:</span>
                                        <span className="ml-2 text-white font-mono">{detail.id}</span>
                                    </div>
                                    <div>
                                        <span className="text-gray-400">{t('User Email')}:</span>
                                        <span className="ml-2 text-white">{detail.user_email}</span>
                                    </div>
                                    {detail.data_object_id && (
                                        <div>
                                            <span className="text-gray-400">{t('Data Object ID')}:</span>
                                            <span className="ml-2 text-white font-mono">{detail.data_object_id}</span>
                                        </div>
                                    )}
                                    {detail.folder_id && (
                                        <div>
                                            <span className="text-gray-400">{t('Folder ID')}:</span>
                                            <span className="ml-2 text-white font-mono">{detail.folder_id}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Raw Report Data */}
                            <div>
                                <details className="group">
                                    <summary className="cursor-pointer text-blue-400 hover:text-blue-300 font-medium">
                                        {t('View Full Analysis Results (JSON)')}
                                    </summary>
                                    <div className="mt-3 bg-black/40 rounded-lg border border-gray-800">
                                        <div className="px-3 py-2 border-b border-gray-800 flex justify-between items-center">
                                            <span className="text-xs text-gray-400">{t('Raw Report Data')}</span>
                                            <button
                                                onClick={() => navigator.clipboard.writeText(JSON.stringify(detail.raw_report, null, 2))}
                                                className="text-xs text-blue-400 hover:text-blue-300"
                                            >
                                                {t('Copy JSON')}
                                            </button>
                                        </div>
                                        <pre className="p-4 max-h-96 overflow-auto text-xs whitespace-pre-wrap break-all text-gray-300">
                                            {JSON.stringify(detail.raw_report, null, 2)}
                                        </pre>
                                    </div>
                                </details>
                            </div>

                            {/* Actions */}
                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                                {detail.data_object_id && (
                                    <a
                                        href={`/report?dataObjectId=${encodeURIComponent(detail.data_object_id)}`}
                                        className="px-6 py-3 rounded bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold transition-colors"
                                    >
                                        {t('View Current Report')}
                                    </a>
                                )}
                                <button
                                    onClick={onClose}
                                    className="px-6 py-3 rounded bg-gray-600 hover:bg-gray-700 text-white text-base font-semibold transition-colors"
                                >
                                    {t('Close')}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const Metric = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <div className="bg-gray-800 rounded-lg p-3 border border-gray-700">
        <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
        <div className="text-lg font-semibold text-white mt-1">{value}</div>
    </div>
);

export const BackendHistoricalReportsPage = () => {
    return (
        <HistoricalReportsErrorBoundary>
            <BackendHistoricalReportsPageContent />
        </HistoricalReportsErrorBoundary>
    );
};

const BackendHistoricalReportsPageContent = () => {
    const { t } = useTranslate();
    const { user } = useAuth();
    const navigate = useNavigate();
    const {
        reports,
        loading,
        error,
        totalCount,
        hasMore,
        currentPage,
        totalPages,
        filters,
        setFilter,
        loadMore,
        goToPage,
        refresh,
        clearError,
        getReportDetail,
    } = useBackendHistoricalReports();

    // Debug logging removed after stabilization
    const [detailId, setDetailId] = useState<string | null>(null);
    const [folderSelect, setFolderSelect] = useState('');
    const [dataObjectSelect, setDataObjectSelect] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Separate state for all folders and data objects (independent of reports)
    const [allFolders, setAllFolders] = useState<Folder[]>([]);
    const [allDataObjects, setAllDataObjects] = useState<DataSource[]>([]);
    const [foldersLoading, setFoldersLoading] = useState(false);
    const [dataObjectsLoading, setDataObjectsLoading] = useState(false);

    // Tab state: 'reports' or 'shared'
    const [activeTab, setActiveTab] = useState<'reports' | 'shared'>('reports');

    // Shared reports data
    const [sharedReports, setSharedReports] = useState<(SaveSharedReportResponse & { dataset_name?: string })[]>([]);
    const [sharedLoading, setSharedLoading] = useState(false);

    useEffect(() => {
        if (activeTab !== 'shared' || !user?.email) return;
        let cancelled = false;
        (async () => {
            setSharedLoading(true);
            try {
                const res = await sharedReportService.getSharedWithMe(50, 0);
                if (cancelled) return;
                const enriched = await Promise.all(
                    res.reports.map(async (r) => {
                        try {
                            const detail = await sharedReportService.getSharedReport(r.report_id);
                            const raw = detail.raw_report as Record<string, unknown>;
                            const desc = raw.data_object_description as Record<string, unknown> | undefined;
                            const name = (raw.dataset_name as string) || (raw.data_object_name as string) || (desc?.dataset_name as string) || (desc?.table_name as string) || undefined;
                            return { ...r, dataset_name: name };
                        } catch { return { ...r }; }
                    })
                );
                if (!cancelled) setSharedReports(enriched);
            } catch { /* ignore */ }
            finally { if (!cancelled) setSharedLoading(false); }
        })();
        return () => { cancelled = true; };
    }, [activeTab, user?.email]);

    const closeDetail = useCallback(() => setDetailId(null), []);

    // Navigate to cinematic report
    const openCinematicReport = useCallback((report: any) => {
        if (report.id) {
            const url = `/report?reportId=${encodeURIComponent(report.id)}`;
            navigate(url);
        }
    }, [navigate]);

    // Helper functions to get names from API response (no more separate API calls needed)
    const getFolderName = useCallback((report: HistoricalReportDetail | any) => {
        // Translate backend root folder sentinel, explicit 'Root' label, or missing value to 'Home screen'
        const name = report.folder_name;
        if (!name || name === '__root__' || name === 'Root') return t('Home screen');
        return name;
    }, []);

    const getDataObjectName = useCallback((report: HistoricalReportDetail | any) => {
        // Use data_object_name from API response
        return report.data_object_name || t('Unknown');
    }, []);

    // Load all folders and data objects from bootstrap
    useEffect(() => {
        const loadBootstrapData = async () => {
            setFoldersLoading(true);
            setDataObjectsLoading(true);
            try {
                const { folders, dataSources } = await fetchDataAdmissionBootstrap(user?.email || '');
                // Add "Home screen" option for root-level data objects
                const unassignedFolder: Folder = {
                    id: '__root__' as any,
                    name: 'Home screen',
                    uuid: '__root__',
                    createdAt: new Date().toISOString(),
                    dataObjectCount: 0
                };
                setAllFolders([unassignedFolder, ...folders]);
                setAllDataObjects(dataSources);
            } catch (error) {
                console.error('Failed to load bootstrap data:', error);
            } finally {
                setFoldersLoading(false);
                setDataObjectsLoading(false);
            }
        };

        loadBootstrapData();
    }, []);

    // Folder options from all folders (not just those with reports)
    const folderOptions = useMemo(() => {
        return allFolders
            .map(folder => ({
                id: folder.uuid, // Use UUID for backend API filtering
                name: folder.name
            }))
            .sort((a, b) => {
                // Ensure 'Home screen' appears first
                if (a.name === 'Home screen') return -1;
                if (b.name === 'Home screen') return 1;
                return a.name.localeCompare(b.name);
            });
    }, [allFolders]);

    // Build set of currently existing data object UUIDs for filtering
    const existingDataObjectUuids = useMemo(() => {
        return new Set(allDataObjects.map(ds => ds.uuid).filter(Boolean));
    }, [allDataObjects]);

    // Data object options from currently existing data objects
    const dataObjectOptions = useMemo(() => {
        return allDataObjects
            .map(ds => ({ id: ds.uuid || '', name: ds.name }))
            .filter(ds => ds.id)
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [allDataObjects]);

    // Sort toggle for generated_at column
    const toggleGeneratedSort = useCallback(() => {
        const currentDirection = filters.order_direction || 'desc';
        const newDirection = currentDirection === 'desc' ? 'asc' : 'desc';
        setFilter('order_by', 'generated_at');
        setFilter('order_direction', newDirection);
    }, [filters.order_direction, setFilter]);

    // Apply date filters dynamically
    useEffect(() => {
        if (fromDate) {
            const fromIso = new Date(fromDate + 'T00:00:00Z').toISOString();
            setFilter('from_date', fromIso);
        } else {
            setFilter('from_date', undefined);
        }
    }, [fromDate, setFilter]);

    useEffect(() => {
        if (toDate) {
            const toIso = new Date(toDate + 'T23:59:59Z').toISOString();
            setFilter('to_date', toIso);
        } else {
            setFilter('to_date', undefined);
        }
    }, [toDate, setFilter]);

    // Filter handlers
    const handleFolderChange = (value: string) => {
        setFolderSelect(value);
        // Handle folder filtering:
        // - "Home screen" (__root__): Don't use server filter, we'll filter client-side for null folder_id
        // - Regular folders: Use server-side filtering with folder UUID
        // - No selection: Show all reports
        if (value === '__root__') {
            // For "Home screen", clear server filter and use client-side filtering
            setFilter('folder_id', undefined);
        } else if (!value) {
            // No folder selected - show all
            setFilter('folder_id', undefined);
        } else {
            // Regular folder UUID - use server-side filtering
            setFilter('folder_id', value);
        }
    };

    const handleDataObjectChange = (value: string) => {
        setDataObjectSelect(value);
        // Client-side filtering by data object UUID in filteredReports
    };

    // Client-side filtering: only show reports for data objects that still exist
    const filteredReports = useMemo(() => {
        // First, exclude reports whose data objects have been deleted
        let filtered = existingDataObjectUuids.size > 0
            ? reports.filter(report => report.data_object_id && existingDataObjectUuids.has(report.data_object_id))
            : reports; // If bootstrap hasn't loaded yet, show all temporarily

        // Apply folder filtering
        if (folderSelect === '__root__') {
            filtered = filtered.filter(report =>
                !report.folder_name || report.folder_name === '__root__' || report.folder_name === 'Root'
            );
        }

        // Apply data object UUID filtering
        if (dataObjectSelect) {
            filtered = filtered.filter(report =>
                report.data_object_id === dataObjectSelect
            );
        }

        return filtered;
    }, [reports, existingDataObjectUuids, folderSelect, dataObjectSelect]);

    // Authentication check
    if (!user) {
        return (
            <div className="container mx-auto px-6 py-8">
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                    <p className="text-yellow-400">{t('Please log in to view reports.')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-6 pt-0 pb-6 flex flex-col min-h-screen notranslate">
            {/* Tab Bar */}
            <div className="flex border-b border-gray-300 mb-4">
                <button
                    onClick={() => setActiveTab('reports')}
                    className={`px-6 py-3 text-sm font-semibold transition-colors ${activeTab === 'reports'
                        ? 'text-red-600 border-b-2 border-red-600'
                        : 'text-gray-500 hover:text-gray-700'}`}
                >
                    {t('My Reports')}
                </button>
                <button
                    onClick={() => setActiveTab('shared')}
                    className={`px-6 py-3 text-sm font-semibold transition-colors flex items-center gap-1.5 ${activeTab === 'shared'
                        ? 'text-red-600 border-b-2 border-red-600'
                        : 'text-gray-500 hover:text-gray-700'}`}
                >
                    <HiShare className="w-4 h-4" />
                    {t('Shared With Me')}
                </button>
            </div>

            {/* Shared Reports Tab */}
            {activeTab === 'shared' && (
                <div className="flex-1 flex flex-col">
                    {sharedLoading ? (
                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                            <span className="text-gray-400 mb-4">{t('Loading shared reports...')}</span>
                            <div className="loader-red loader-md"></div>
                        </div>
                    ) : sharedReports.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 gap-4">
                            <HiShare className="w-16 h-16 text-gray-300" />
                            <p className="text-lg text-gray-500">{t('No shared reports yet')}</p>
                            <p className="text-sm text-gray-400">{t('Reports shared with you will appear here')}</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-lg border border-gray-300 overflow-hidden flex flex-col flex-1">
                            <div className="flex-1 overflow-auto focus:outline-none scroll-smooth" role="region" aria-label="Shared reports table" tabIndex={0}>
                                <table className="w-full text-sm">
                                    <thead className="bg-red-600 text-white sticky top-0 z-10">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">{t('Data Object')}</th>
                                            <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">{t('Shared By')}</th>
                                            <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">{t('Health Score')}</th>
                                            <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">{t('Issues')}</th>
                                            <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">{t('Generated')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                        {sharedReports.map((report) => (
                                            <tr
                                                key={report.share_id}
                                                className="hover:bg-red-50 cursor-pointer transition-colors"
                                                onClick={() => navigate(`/shared-report?reportId=${report.report_id}`)}
                                                tabIndex={0}
                                                role="button"
                                                aria-label={`View shared report ${report.dataset_name || 'report'}`}
                                            >
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <TbReportAnalytics className="w-5 h-5 text-red-600 flex-shrink-0" />
                                                        <span className="text-sm text-gray-800 font-medium notranslate">{report.dataset_name || t('Shared Report')}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    <span className="text-sm text-gray-700">{report.shared_by_email}</span>
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    <span className="text-sm text-gray-700 font-semibold">{formatHealthScore(report.health_score_overall)}</span>
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    <span className="text-sm text-gray-700">{report.issue_count}</span>
                                                </td>
                                                <td className="px-6 py-3 whitespace-nowrap">
                                                    <span className="text-sm text-gray-500">{formatDate(report.report_generated_at)}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="px-6 pb-4 text-right border-t border-gray-200 bg-white">
                                <p className="text-xs text-gray-500">{t('Showing')} {sharedReports.length} {t('reports')}</p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* My Reports Tab */}
            {activeTab === 'reports' && (<>
            {/* Filters */}
            <div className="bg-white rounded-lg border border-gray-300 p-3 mb-2 shadow-sm">
                <div className="flex items-center justify-end mb-2">
                    <ContextualHelpLink videoId="tv-006" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                    {/* Folder Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('Folder')}
                        </label>
                        <select
                            value={folderSelect}
                            onChange={(e) => handleFolderChange(e.target.value)}
                            disabled={foldersLoading}
                            className="w-full rounded-lg bg-white border border-red-300 text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <option value="">{foldersLoading ? t('Loading folders...') : t('All Folders')}</option>
                            {folderOptions.map(folder => (
                                <option key={folder.id} value={folder.id}>
                                    {folder.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Data Object Filter */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('Data Object')}
                        </label>
                        <select
                            value={dataObjectSelect}
                            onChange={(e) => handleDataObjectChange(e.target.value)}
                            disabled={dataObjectsLoading}
                            className="w-full rounded-lg bg-white border border-red-300 text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <option value="">{dataObjectsLoading ? t('Loading data objects...') : t('All Data Objects')}</option>
                            {dataObjectOptions.map(dataObject => (
                                <option key={dataObject.id} value={dataObject.id}>
                                    {dataObject.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* From Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('From Date')}
                        </label>
                        <input
                            type="date"
                            value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            className="w-full rounded-lg bg-white border border-red-300 text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                    </div>

                    {/* To Date */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {t('To Date')}
                        </label>
                        <input
                            type="date"
                            value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            className="w-full rounded-lg bg-white border border-red-300 text-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                        />
                    </div>
                </div>
            </div>

            {/* Error Display */}
            {error && (
                <HistoricalReportsErrorDisplay
                    error={error}
                    onRetry={refresh}
                    onDismiss={clearError}
                    className="mb-4"
                />
            )}

            {/* Loading State */}
            {loading && filteredReports.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12">
                    <span className="text-gray-400 mb-4">{t('Loading reports...')}</span>
                    <div className="loader-red loader-md"></div>
                </div>
            )}

            {/* Results Summary (moved below table) */}

            {/* Reports Table - wrapped in flex-1 container */}
            {reports.length > 0 && (
                <div className="flex-1 flex flex-col">
                    <div className="bg-white rounded-lg border border-gray-300 overflow-hidden flex flex-col flex-1">
                        {/* Scrollable results region: expands to fill available space */}
                        <div
                            className="flex-1 overflow-auto focus:outline-none scroll-smooth"
                            role="region"
                            aria-label="Historical reports table"
                            tabIndex={0}
                        >
                            <table className="w-full text-sm">
                                <thead className="bg-red-600 text-white sticky top-0 z-10">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">
                                            {t('Data Object')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">
                                            {t('Folder')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">
                                            {t('Health Score')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">
                                            {t('Issues')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">
                                            {t('Status')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-[11px] font-semibold tracking-wider">
                                            <button
                                                onClick={toggleGeneratedSort}
                                                className="flex items-center gap-1 hover:underline"
                                            >
                                                {t('Generated')}
                                                <span className="ml-0.5">
                                                    {filters.order_direction === 'asc' ? '↑' : '↓'}
                                                </span>
                                            </button>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {filteredReports.map((report) => (
                                        <tr
                                            key={report.id}
                                            className="hover:bg-red-50 cursor-pointer transition-colors"
                                            onClick={() => openCinematicReport(report)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    openCinematicReport(report);
                                                }
                                            }}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`View cinematic report for ${getDataObjectName(report)}`}
                                        >
                                            <td className="px-6 py-3 whitespace-nowrap">
                                                <div className="text-sm text-gray-800 font-medium notranslate">
                                                    {getDataObjectName(report)}
                                                </div>
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap">
                                                <span className="text-sm text-gray-700">
                                                    {getFolderName(report)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap">
                                                <span className="text-sm text-gray-700 font-semibold">
                                                    {formatHealthScore(report.health_score_overall)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap">
                                                <span className="text-sm text-gray-700">
                                                    {report.issue_count || 0}
                                                </span>
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap">
                                                <StatusBadge status={report.status} />
                                            </td>
                                            <td className="px-6 py-3 whitespace-nowrap">
                                                <span className="text-sm text-gray-500">
                                                    {formatDate(report.generated_at)}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {/* Pagination control remains outside scrollable area */}
                        {!loading && (
                            <div className="px-6 pb-4 text-right border-t border-gray-200 bg-white">
                                <p className="text-xs text-gray-500">
                                    {t('Showing')} {filteredReports.length} {t('reports')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Empty State */}
            {!loading && filteredReports.length === 0 && (
                <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-white mb-1">
                        {t('No reports found')}
                    </h3>
                    <p className="text-gray-400">
                        {t('Try adjusting your filters or creating some data analysis reports.')}
                    </p>
                </div>
            )};

            {/* Detail Modal */}
            <DetailModal
                open={detailId !== null}
                onClose={closeDetail}
                reportId={detailId}
                loadDetail={getReportDetail}
            />
            </>)}
        </div>
    );
};